import type { Chord, NoteEvent } from '@tunasong/music-lib'
import { chordToEvents } from '../player/chord-to-events.js'
import { useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Reharm Lab v1 N-chord audition playback. Each chord plays as a half-note,
 * separated by GAP_MS. Supports N = 4 (no insertion) or N = 5 (secondary-
 * dominant insertion active). One-shot.
 *
 * Spec: docs/superpowers/specs/2026-05-19-reharm-lab-design.md §4 (playback)
 */
const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM // 600 ms
const HALF_NOTE_MS = BEAT_MS * 2 // 1200 ms — each chord plays as a half-note
const GAP_MS = 200 // brief release between chord events
const STEP_MS = HALF_NOTE_MS + GAP_MS // 1400 ms total per chord-slot

export interface ReharmStab {
  events: NoteEvent[]
  delayMs: number
}

/** Build the N-chord audition. Pure: deterministic given the chord list. */
export const buildReharmAuditionStabs = (chords: readonly Chord[]): ReharmStab[] =>
  chords.map((chord, i) => ({
    events: chordToEvents(chord, '2n'),
    delayMs: i * STEP_MS,
  }))

export interface UseReharmLabPlaybackProps {
  chords: readonly Chord[]
}

export interface ReharmLabPlayback {
  play: () => void
  stop: () => void
}

export const useReharmLabPlayback = (props: UseReharmLabPlaybackProps): ReharmLabPlayback => {
  const { play, stop } = useStabScheduler({
    buildStabs: () => buildReharmAuditionStabs(props.chords),
  })
  return { play, stop }
}
